import type { ClientsDetails, Policy } from '@/types/portal'
import {
  formatLongDate,
  formatLongDateFromDate,
  formatLocaleDate,
  formatShortDate,
  formatSidebarDate,
  formatTodayForFileName,
  getAgeFromBirthDate,
  isActiveTrip,
  isAfterToday,
  isOnOrAfter,
  isOnOrAfterToday,
  isOnOrBefore,
  isSameCalendarDay,
  minDateString,
  parsePolicyDate,
  startOfDay,
  today,
  toLocalDateString,
} from '@/utils/dateUtils'

export {
  formatLongDate,
  formatLongDateFromDate,
  formatLocaleDate,
  formatShortDate,
  formatSidebarDate,
  formatTodayForFileName,
  isActiveTrip,
  isAfterToday,
  isOnOrAfter,
  isOnOrAfterToday,
  isOnOrBefore,
  isSameCalendarDay,
  minDateString,
  parsePolicyDate,
  startOfDay,
  today,
  toLocalDateString,
}

export function isMultiTripPolicy(policy?: Policy | null) {
  return policy?.isMultiTrip === true
}

export function isSingleTripPolicy(policy?: Policy | null) {
  if (!policy) {
    return false
  }
  return policy.optionEnum === 'SingleTrip' || policy.option === 'Single trip'
}

export function isSamePersonId(left?: string | null, right?: string | null) {
  if (!left || !right) {
    return false
  }
  return left.toLowerCase() === right.toLowerCase()
}

export function isClientPerson(clientsDetails: ClientsDetails | null, personId?: string | null) {
  if (!clientsDetails || !personId) {
    return false
  }
  return isSamePersonId(personId, clientsDetails.clientId) || isSamePersonId(personId, clientsDetails.client.id)
}

export function isSpousePerson(clientsDetails: ClientsDetails | null, personId?: string | null) {
  if (!clientsDetails || !personId) {
    return false
  }
  return isSamePersonId(personId, clientsDetails.spouse?.id)
}

export function getTravelerAge(traveler: { age?: number; birthDate?: string }) {
  if (traveler.age != null) {
    return traveler.age
  }
  return getAgeFromBirthDate(traveler.birthDate)
}

export function getClientFullName(clientsDetails: ClientsDetails | null, personId?: string | null) {
  if (!clientsDetails || !personId) {
    return ''
  }
  if (clientsDetails.clientId?.toLowerCase() === personId.toLowerCase()) {
    return `${clientsDetails.client.firstName} ${clientsDetails.client.lastName}`
  }
  return `${clientsDetails.spouse?.firstName ?? ''} ${clientsDetails.spouse?.lastName ?? ''}`.trim()
}

export function getClientFirstName(clientsDetails: ClientsDetails | null, personId?: string | null) {
  if (!clientsDetails || !personId) {
    return clientsDetails?.client.firstName ?? ''
  }
  var normalizedPersonId = personId.toLowerCase()
  if (
    clientsDetails.clientId?.toLowerCase() === normalizedPersonId
    || clientsDetails.client.id.toLowerCase() === normalizedPersonId
  ) {
    return clientsDetails.client.firstName
  }
  if (clientsDetails.spouse?.id?.toLowerCase() === normalizedPersonId) {
    return clientsDetails.spouse.firstName
  }
  return clientsDetails.spouse?.firstName ?? ''
}

export function downloadFileFromBuffer(buffer: ArrayBuffer, fileName: string) {
  var blob = new Blob([buffer], { type: 'application/pdf' })
  var url = URL.createObjectURL(blob)
  var link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

export type PolicyStatus = 'Pending' | 'Approved' | 'Active' | 'Expired' | 'PaymentNeeded' | 'Other' | 'Cancelled' | 'UpdatePending';

var transactionStatusNames = ['Pending', 'Finalized', 'DepositPending', 'Standby', 'Approved', 'CreditCardDenied', 'Denied', 'UpdatePending'] as const
var transactionTypeNames = ['Sale', 'DateChange', 'Extension', 'EarlyReturn', 'Cancellation', 'MQChange'] as const

function readPolicyField(policy: Record<string, unknown>, ...keys: string[]) {
  for (var key of keys) {
    var value = policy[key]
    if (value != null && value !== '') {
      return value
    }
  }
  return undefined
}

function readPolicyEnumField(policy: Record<string, unknown>, keys: string[], names: readonly string[]) {
  var value = readPolicyField(policy, ...keys)
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' && value >= 0 && value < names.length) {
    return names[value]
  }
  return undefined
}

export function normalizePolicy(policy: Policy): Policy {
  var raw = policy as Record<string, unknown>
  return {
    ...policy,
    transactionStatus: readPolicyEnumField(raw, ['transactionStatus', 'TransactionStatus'], transactionStatusNames),
    transactionType: readPolicyEnumField(raw, ['transactionType', 'TransactionType'], transactionTypeNames),
    departureDate: (readPolicyField(raw, 'departureDate', 'DepartureDate') as string | undefined) ?? policy.departureDate,
    effectiveDate: (readPolicyField(raw, 'effectiveDate', 'EffectiveDate') as string | undefined) ?? policy.effectiveDate,
    returnDate: (readPolicyField(raw, 'returnDate', 'ReturnDate') as string | undefined) ?? policy.returnDate,
    productType: (readPolicyField(raw, 'productType', 'ProductType') as Policy['productType']) ?? policy.productType,
    isMultiTrip: (readPolicyField(raw, 'isMultiTrip', 'IsMultiTrip') as boolean | undefined) ?? policy.isMultiTrip,
    isManual: (readPolicyField(raw, 'isManual', 'IsManual') as boolean | undefined) ?? policy.isManual,
    _ts: (readPolicyField(raw, '_ts') as number | undefined) ?? policy._ts,
  }
}

export function getPolicyStatus(policy: Policy): PolicyStatus {
  var normalized = normalizePolicy(policy)
  if (normalized.transactionType === 'Cancellation') {
    return 'Cancelled'
  }
  if (normalized.transactionStatus === 'Finalized' || normalized.transactionStatus === 'DepositPending') {
    var now = today()
    var departureDate = parsePolicyDate(normalized.departureDate)
    var returnDate = parsePolicyDate(normalized.returnDate)
    // Trip-value products (TC / All Inclusive / Comprehensive): Agent App treats these as
    // active until ReturnDate passes. Do not require EffectiveDate <= today — date changes
    // can leave EffectiveDate in the future when the offset from departure is preserved.
    if (isTripValueProductType(normalized.productType)) {
      if (returnDate && returnDate >= now) {
        return 'Active'
      }
      return 'Expired'
    }
    if (departureDate && returnDate && departureDate <= now && returnDate >= now) {
      return 'Active'
    }
    if (normalized.productType === 'Medical' && departureDate && departureDate > now) {
      return 'Approved'
    }
    return 'Expired'
  }
  if (normalized.transactionStatus === 'CreditCardDenied') {
    return 'PaymentNeeded'
  }
  if (normalized.transactionStatus === 'UpdatePending') {
    return 'UpdatePending'
  }
  return 'Other'
}

function isTripValueProductType(productType?: string | null) {
  return productType === 'AllInclusive'
    || productType === 'TripCancellation'
    || productType === 'Comprehensive'
}

export function isTripValueProduct(productType?: string | null) {
  return isTripValueProductType(productType)
}

export function isActivePolicyStatus(status: PolicyStatus) {
  return status === 'Active' || status === 'Approved'
}
